import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  Box,
  EmptyState,
  ScrollArea,
  Separator,
} from "@salestrak/ui";
import { type CellState } from "@salestrak/permissions";
import { PermissionsSection } from "#app/components/settings/permissions/permissions-section";
import { PermissionsMatrixSkeleton } from "#app/components/settings/permissions/matrix-chrome";
import { usePermissionMatrix } from "#app/lib/hooks/use-permission-matrix";
import { PageHeader } from "#app/components/page-header";
import { withPermission } from "#app/components/permissions/require-permission";
import { AdvancedPermissionsSection } from "#app/components/settings/permissions/advanced-permissions-section";
import { CustomRolePermissionsSection } from "#app/components/settings/permissions/custom-role-permissions-section";
import { InviteGrantMatrix } from "#app/components/settings/permissions/invite-grant-matrix";
import { RolesSection } from "#app/components/settings/permissions/roles-section";
import {
  DeleteRoleDialog,
  RenameRoleDialog,
} from "#app/components/settings/role-actions-dialogs";
import { useWorkspace } from "#app/lib/data/app-provider";
import { useCustomRoleActions } from "#app/lib/hooks/use-custom-role-actions";
import { useInviteGrantMatrix } from "#app/lib/hooks/use-invite-grant-matrix";

function SettingsPermissionsPage() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? "";

  const { loading, matrix, toggle } = useInviteGrantMatrix(workspaceId);

  // Full role list (system + custom) for the delete-role reassignment
  // picker. Apollo dedupes this against the same query inside
  // CustomRolePermissionsSection, so it's not a second network round-trip.
  const { matrix: permMatrix } = usePermissionMatrix(workspaceId);
  const allRoles = useMemo(() => permMatrix?.roles ?? [], [permMatrix]);

  // Custom-role rename / delete — shared across both matrices' column
  // header menus, so the dialog state + mutations live at page level.
  const { renameRole, deleteRole } = useCustomRoleActions(workspaceId);
  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const handleRenameRole = useCallback(
    (role: { id: string; roleName: string }) =>
      setRenameTarget({ id: role.id, name: role.roleName }),
    [],
  );
  const handleDeleteRole = useCallback(
    (role: { id: string; roleName: string }) =>
      setDeleteTarget({ id: role.id, name: role.roleName }),
    [],
  );

  // The API returns the roles worth showing per axis: inviterRoles are the
  // columns, invitableRoles the rows. (Owner is dropped from both; Admin is
  // a column but not a row, since Owner above it is fully locked.)
  const inviterRoles = matrix?.inviterRoles ?? [];
  const invitableRoles = matrix?.invitableRoles ?? [];

  // `${inviterId}:${invitableId}` → cell state, straight from the DB.
  const stateByCell = useMemo(() => {
    const map = new Map<string, CellState>();
    for (const g of matrix?.grants ?? []) {
      map.set(`${g.inviterRoleId}:${g.invitableRoleId}`, g.state as CellState);
    }
    return map;
  }, [matrix]);

  return (
    <Box direction="column" fill="flex">
      <PageHeader title="Permissions" />
      <Separator />
      <ScrollArea>
        {/* One rhythm for the whole page: 32px between a section and the rule
            beside it, so a section break reads as ~64px of air, and every
            section's own header sits 24px off its content. */}
        <Box padding="lg" gap="xl">
          <RolesSection />
          <Separator />
          <PermissionsSection icon="user" title="Invite Permissions">
            {loading && !matrix ? (
              // The settled axes: rows are the invitable roles (Guest /
              // Limited Member / Member — Owner and Admin are never
              // invitable targets), columns the inviter roles.
              <PermissionsMatrixSkeleton rows={3} />
            ) : inviterRoles.length === 0 ? (
              <EmptyState size="xs" title="No roles found." />
            ) : (
              <InviteGrantMatrix
                inviterRoles={inviterRoles}
                invitableRoles={invitableRoles}
                stateByCell={stateByCell}
                onToggle={toggle}
                onRenameRole={handleRenameRole}
                onDeleteRole={handleDeleteRole}
              />
            )}
          </PermissionsSection>
          <Separator />
          <CustomRolePermissionsSection
            workspaceId={workspaceId}
            onRenameRole={handleRenameRole}
            onDeleteRole={handleDeleteRole}
          />
          <Separator />
          <AdvancedPermissionsSection workspaceId={workspaceId} />
        </Box>
      </ScrollArea>
      <RenameRoleDialog
        open={renameTarget != null}
        onOpenChange={(next) => {
          if (!next) setRenameTarget(null);
        }}
        currentName={renameTarget?.name ?? ""}
        onConfirm={async (nextName) => {
          if (renameTarget) await renameRole(renameTarget.id, nextName);
        }}
      />
      <DeleteRoleDialog
        open={deleteTarget != null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        roleName={deleteTarget?.name ?? ""}
        reassignRoles={allRoles.filter((r) => r.id !== deleteTarget?.id)}
        onConfirm={async (reassignToRoleId) => {
          if (deleteTarget) await deleteRole(deleteTarget.id, reassignToRoleId);
        }}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/permissions")({
  component: withPermission("create.roles", SettingsPermissionsPage),
});
